import React from 'react';
import { CloudArrowUp, FileText, ShieldCheck, Clock, WarningCircle, SealCheck } from '@phosphor-icons/react';
import type { VerificationState } from '../../lib/utils';
interface StepVerificationProps {
  verificationFile: File | null;
  verificationStatus?: VerificationState;
  ownerName?: string;
  onChange: (file: File | null) => void;
}
export function StepVerification({
  verificationFile,
  verificationStatus,
  ownerName,
  onChange
}: StepVerificationProps) {
  const previewName = ownerName?.trim() ? ownerName.trim() : 'Numele tău';
  return <div className="animate-in fade-in slide-in-from-bottom-2 duration-300 space-y-6" data-id="element-2733">
      <div data-id="element-2734">
        <h2 className="text-2xl sm:text-3xl font-bold text-light-1 mb-2" data-id="element-2735">
          Verifică-te ca proprietar{' '}
          <span className="text-light-4 text-base font-normal" data-id="element-2736">(opțional)</span>
        </h2>
        <p className="text-sm text-light-3 leading-relaxed" data-id="element-2737">
          Proprietarii verificați primesc un badge de încredere care apare lângă
          numele lor în fiecare ofertă și anunț. Verificarea proprietății este
          pasul prin care obții acest badge.
        </p>
      </div>

      {/* Badge preview — shows the landlord exactly what the badge looks
              like next to their name once verified. */}
      <div className="rounded-xl bg-dark-2 border border-dark-3 px-4 py-3 flex items-center gap-3" data-id="element-2738">
        <div className="w-9 h-9 rounded-full bg-dark-3 border border-dark-4 inline-flex items-center justify-center text-light-2 text-sm font-medium shrink-0" data-id="element-2739">
          {previewName[0]}
        </div>
        <div className="min-w-0 flex-1" data-id="element-2740">
          <div className="flex items-center gap-1.5" data-id="element-2741">
            <span className="text-sm font-medium text-light-1 truncate" data-id="element-2742">
              {previewName}
            </span>
            <SealCheck size={16} weight="fill" className="text-lime-1 shrink-0" aria-label="Badge de proprietar verificat" data-id="element-2743" />
          </div>
          <p className="text-xs text-light-4 mt-0.5" data-id="element-2744">
            Așa vei apărea după verificare.
          </p>
        </div>
      </div>

      <div className="bg-dark-2 border border-dark-3 rounded-2xl p-6" data-id="element-2745">
        {verificationStatus?.status === 'verified' && <div className="flex items-center gap-3 p-4 rounded-xl bg-lime-5/10 border border-lime-5/20 text-lime-1" data-id="element-2746">
            <ShieldCheck size={24} weight="fill" data-id="element-2747" />
            <div data-id="element-2748">
              <p className="text-sm font-medium" data-id="element-2749">Proprietar verificat</p>
              <p className="text-xs text-lime-1/70 mt-0.5" data-id="element-2750">
                Actele au fost validate de echipa noastră.
              </p>
            </div>
          </div>}

        {verificationStatus?.status === 'pending' && !verificationFile && <div className="flex items-center gap-3 p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-400" data-id="element-2751">
            <Clock size={24} weight="fill" data-id="element-2752" />
            <div data-id="element-2753">
              <p className="text-sm font-medium" data-id="element-2754">În curs de verificare</p>
              <p className="text-xs text-amber-400/70 mt-0.5" data-id="element-2755">
                Documentele tale sunt analizate (max 24h).
              </p>
            </div>
          </div>}

        {(!verificationStatus || ['unverified', 'rejected', 'expired'].includes(verificationStatus.status) || verificationFile) && <div className="space-y-4" data-id="element-2756">
            {verificationStatus?.status === 'rejected' && !verificationFile && <div className="flex items-start gap-2 p-3 rounded-md bg-red-500/10 border border-red-500/20 text-red-400" data-id="element-2757">
                <WarningCircle size={18} weight="fill" className="shrink-0 mt-0.5" data-id="element-2758" />
                <p className="text-xs" data-id="element-2759">
                  Verificarea a fost respinsă. Te rugăm să încarci un document
                  clar și complet.
                </p>
              </div>}

            <p className="text-sm text-light-3" data-id="element-2760">
              Încarcă un Extras de Carte Funciară sau Contract de
              vânzare-cumpărare. Documentul este folosit doar pentru verificarea
              identității tale ca proprietar.
            </p>

            {verificationFile ? <div className="flex items-center justify-between p-3 rounded-md bg-dark-3 border border-dark-4" data-id="element-2761">
                <div className="flex items-center gap-3 min-w-0" data-id="element-2762">
                  <FileText size={20} className="text-light-2 shrink-0" data-id="element-2763" />
                  <span className="text-sm text-light-1 truncate" data-id="element-2764">
                    {verificationFile.name}
                  </span>
                </div>
                <button type="button" onClick={() => onChange(null)} className="text-xs text-light-4 hover:text-red-400 transition-colors px-2 py-1" data-id="element-2765">
                  Elimină
                </button>
              </div> : <label className="flex flex-col items-center justify-center text-center px-4 py-8 rounded-xl border-2 border-dashed border-dark-4 bg-dark-1 hover:border-light-4 hover:bg-dark-3 cursor-pointer transition-colors" data-id="element-2766">
                <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={e => onChange(e.target.files?.[0] || null)} data-id="element-2767" />
                <CloudArrowUp size={32} className="text-light-3 mb-3" data-id="element-2768" />
                <p className="text-sm font-medium text-light-1" data-id="element-2769">
                  Apasă pentru a încărca documentul
                </p>
                <p className="text-xs text-light-4 mt-1" data-id="element-2770">
                  PDF, JPG, PNG · max 10MB
                </p>
              </label>}
          </div>}
      </div>
    </div>;
}